import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const TestimonialsScroll = () => {
    const sectionRef = useRef(null);
    const cardsRef = useRef([]);

    const testimonials = [
        {
            name: 'Himani Kota',
            country: 'Australia',
            text: 'Swan Global helped me achieve my dream of studying in Australia. The visa process was smooth and hassle-free. Highly recommended!',
            university: 'Verified Student',
            course: 'Australia Admission & Visa',
        },
        {
            name: 'Srinivas Reddy',
            country: 'Australia',
            text: 'I had a great experience with Swan Global. Their guidance for Australia admission and visa was excellent.',
            university: 'Verified Student',
            course: 'Australia Admission & Visa',
        },
        {
            name: 'Bharat Gaddam',
            country: 'New Zealand',
            text: 'Thanks to Swan Global, I got my New Zealand student visa without any issues. The team was very supportive throughout.',
            university: 'Verified Student',
            course: 'New Zealand Admission & Visa',
        },
        {
            name: 'Vamshi Bogapathi',
            country: 'New Zealand',
            text: 'Very professional consultancy. They helped me choose the right university in New Zealand and guided me step by step.',
            university: 'Verified Student',
            course: 'New Zealand Admission & Visa',
        },
        {
            name: 'Vemula Dharmendhar',
            country: 'UK',
            text: 'Swan Global made my UK admission process very easy. I received my offer letter and visa on time.',
            university: 'Verified Student',
            course: 'UK Admission & Visa',
        },
        {
            name: 'Ranjith Reddy Marri',
            country: 'UK',
            text: 'Excellent support from the team. They handled my UK application and visa process smoothly.',
            university: 'Verified Student',
            course: 'UK Admission & Visa',
        },
        {
            name: 'Abhilash Reddy Deva',
            country: 'USA',
            text: 'I am thankful to Swan Global for guiding me through my USA university application process. Very helpful and knowledgeable team.',
            university: 'Verified Student',
            course: 'USA Admission & Visa',
        },
        {
            name: 'Sindhu Vemula',
            country: 'USA',
            text: 'They provided great support for my USA admission and visa preparation. Highly recommended!',
            university: 'Verified Student',
            course: 'USA Admission & Visa',
        },
        {
            name: 'Vishnu Vardhan Pollishety',
            country: 'Canada',
            text: 'Swan Global made my Canada visa process simple and stress-free. I am happy with their service.',
            university: 'Verified Student',
            course: 'Canada Admission & Visa',
        },
        {
            name: 'Ankit Jain',
            country: 'Canada',
            text: 'I was looking for the best study options in Canada, and Swan Global guided me perfectly. Great experience!',
            university: 'Verified Student',
            course: 'Canada Admission & Visa',
        },
        {
            name: 'Sushama Chillukuri',
            country: 'Ireland',
            text: 'I approached Swan Global for Ireland admissions. From university selection to visa approval, everything was handled professionally.',
            university: 'Verified Student',
            course: 'Ireland Admission & Visa',
        },
        {
            name: 'Bontha Venkat',
            country: 'UK',
            text: 'From the first counseling session to final visa filing, Swan Global gave clear guidance at every stage.',
            university: 'Verified Student',
            course: 'UK Admission & Visa',
        },
        {
            name: 'Ravinder Enugu',
            country: 'Germany',
            text: 'They provided great support for my Germany admission and visa preparation. Highly recommended!',
            university: 'Verified Student',
            course: 'Germany Admission & Visa',
        },
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            cardsRef.current.forEach((card, index) => {
                gsap.fromTo(
                    card,
                    {
                        opacity: 0,
                        x: index % 2 === 0 ? -100 : 100,
                        scale: 0.9,
                    },
                    {
                        opacity: 1,
                        x: 0,
                        scale: 1,
                        duration: 1,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 85%',
                            end: 'top 50%',
                            scrub: 1,
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="testimonials"
            className="py-20 bg-white"
            style={{ minHeight: '50vh' }}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <i className="fas fa-quote-left text-primary-red text-2xl"></i>
                        <h2 className="section-title">Student Success Stories</h2>
                    </div>
                    <p className="section-subtitle">
                        Real stories from students who achieved their dreams
                    </p>
                </div>

                {/* Testimonials */}
                <div className="space-y-16">
                    {testimonials.map((testimonial, index) => (
                        <div
                            key={index}
                            ref={(el) => (cardsRef.current[index] = el)}
                            className={`testimonial-scroll-card flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                                } items-center gap-8`}
                        >
                            {/* Content */}
                            <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-left' : 'lg:text-right'}`}>
                                {/* Stars */}
                                <div className={`flex gap-1 mb-4 ${index % 2 === 0 ? '' : 'lg:justify-end'}`}>
                                    {[...Array(5)].map((_, i) => (
                                        <i key={i} className="fas fa-star text-yellow-400"></i>
                                    ))}
                                </div>

                                {/* Quote */}
                                <blockquote className="text-lg md:text-xl text-gray leading-relaxed mb-6 italic">
                                    "{testimonial.text}"
                                </blockquote>

                                {/* Author Info */}
                                <div className={`flex flex-col ${index % 2 === 0 ? '' : 'lg:items-end'}`}>
                                    <h4 className="text-xl font-bold text-black">{testimonial.name}</h4>
                                    <p className="text-primary-red font-semibold">{testimonial.country}</p>
                                    <p className="text-gray text-sm mt-1">
                                        {testimonial.course} at {testimonial.university}
                                    </p>
                                </div>
                            </div>

                            {/* Decorative Element */}
                            <div className="hidden lg:block absolute -z-10">
                                <div
                                    className={`w-64 h-64 bg-primary-red/5 rounded-full ${index % 2 === 0 ? '-left-32' : '-right-32'
                                        }`}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className="text-center mt-16">
                    <p className="text-gray text-lg mb-6">
                        Join thousands of successful students
                    </p>
                    <a
                        href="https://wa.me/919000329999?text=Hi%2C%20I%20would%20like%20to%20start%20my%20study%20abroad%20journey."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-2"
                    >
                        <i className="fas fa-arrow-right"></i>
                        Start Your Success Story
                    </a>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsScroll;

